import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import storage from '../utils/storage'
import { auth as authApi } from '../api/client'

const AuthContext = createContext()

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => authApi.getStoredClient())
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [hasOnboarded, setHasOnboarded] = useState(() => storage.get('onboarded', true))
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false) // login-specific loading

  // Validate session on mount or auto-login via URL token
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const urlToken = params.get('token')

    if (urlToken) {
      // Auto-login via magic link token — ALWAYS clear previous session first
      window.history.replaceState({}, '', window.location.pathname + window.location.hash)
      // Clear stale user immediately so no wrong data flashes
      setUser(null)
      authApi.loginWithToken(urlToken)
        .then(data => {
          if (data.success && data.client) {
            setUser(data.client)
            setIsLoggedIn(true)
            setHasOnboarded(storage.get('onboarded', true))
          }
        })
        .catch(() => {
          authApi.clearAuth()
        })
        .finally(() => setIsLoading(false))
    } else if (authApi.getToken()) {
      // Validate existing session token — verify client identity matches
      const cachedClient = authApi.getStoredClient()
      authApi.getSession()
        .then(data => {
          if (data?.client) {
            // SECURITY: If the session's client doesn't match cached client, this is
            // a stale token from a different client. Clear everything and re-auth.
            if (!cachedClient?.id || !data.client?.id || cachedClient.id !== data.client.id) {
              authApi.clearAuth()
              setUser(null)
              setIsLoggedIn(false)
              return
            }
            setUser(data.client)
            authApi.setStoredClient(data.client)
            setIsLoggedIn(true)
          } else {
            authApi.clearAuth()
            setUser(null)
          }
        })
        .catch(() => {
          authApi.clearAuth()
          setUser(null)
        })
        .finally(() => setIsLoading(false))
    } else {
      // No token at all
      setIsLoading(false)
    }
  }, [])

  const login = useCallback(async (email, password) => {
    setLoading(true)
    setError(null)
    try {
      const data = await authApi.login(email, password)
      if (data.success && data.client) {
        setUser(data.client)
        setIsLoggedIn(true)
        return data
      }
      throw new Error(data.error || 'Login failed')
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const loginWithToken = useCallback(async (token) => {
    setLoading(true)
    setError(null)
    try {
      const data = await authApi.loginWithToken(token)
      if (data.success && data.client) {
        setUser(data.client)
        setIsLoggedIn(true)
        return data
      }
      throw new Error(data.error || 'Login failed')
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const completeOnboarding = (profile) => {
    setHasOnboarded(true)
    storage.set('onboarded', true)
    if (profile) {
      const updated = { ...user, ...profile }
      setUser(updated)
      authApi.setStoredClient(updated)
    }
  }

  const logout = () => {
    authApi.logout()
    // Clear all client-specific cached data to prevent data leaking
    // Keys stored both with and without storage module's "f_" prefix
    const keysToRemove = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && (
        key.startsWith('f_') ||
        key.startsWith('forge_') ||
        key.startsWith('mc_') || key.startsWith('fl_') || key.startsWith('mp_') ||
        key.startsWith('wt_') || key.startsWith('wh_') || key.startsWith('sc_') ||
        key.startsWith('snap_') || key.startsWith('coach_') ||
        key === 'cd' || key === 'active_session' || key === 'onboarded' || key === 'ob_data'
      )) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k))
    setIsLoggedIn(false)
    setHasOnboarded(false)
    setUser(null)
  }

  const updateUser = (data) => {
    const updated = { ...user, ...data }
    setUser(updated)
    authApi.setStoredClient(updated)
  }

  // Cross-tab auth sync: detect when another tab logs in/out via localStorage changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'forge_v2_client' && e.storageArea === localStorage) {
        if (!e.newValue) {
          // Another tab logged out — clear this tab too
          setUser(null)
          setIsLoggedIn(false)
          setHasOnboarded(false)
        } else {
          try {
            const newClient = JSON.parse(e.newValue)
            if (user && newClient?.id !== user.id) {
              // Different client logged in on another tab — reload to pick up new state
              window.location.reload()
            }
          } catch { /* ignore parse errors */ }
        }
      } else if (e.key === 'forge_v2_session_token' && !e.newValue) {
        // Token removed in another tab
        setUser(null)
        setIsLoggedIn(false)
      }
    }
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [user])

  return (
    <AuthContext.Provider value={{
      isLoggedIn, isLoading, hasOnboarded, user, loading, error,
      login, loginWithToken, completeOnboarding, logout, updateUser
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
